import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface Follower {
  fid: number;
  username: string;
  displayName: string;
  pfpUrl: string;
  followerCount: number;
  followingCount: number;
  powerBadge: boolean;
}

/**
 * Fetch recent followers via get-farcaster-user-followers action proxy.
 * Used as a fallback for farcaster-new-follower trigger.
 *
 * Requires targetFids in triggerPayload to know whose followers to fetch.
 * Returns items shaped like the trigger's resultSchema:
 *   { triggeredAt, eventType: "follow.created", body: { user: {...} } }
 */
export const farcasterFollowerFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;

  const targetFids = triggerPayload.targetFids as number[] | undefined;
  // Use configured FID, or fall back to dwr.eth (FID 3, Farcaster co-founder) for demo data
  const fid = targetFids?.[0] ?? 3;

  const response = await authenticatedFetch<{
    code: number;
    data: { result: { followers: Follower[] }; success: boolean };
  }>(`${V2_ENDPOINT}/v1/action-proxy/get-farcaster-user-followers/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload: { fid, limit: 5 } }),
  });

  if (!response.ok) return [];
  const data = await response.json();
  if (data.code !== 0 || !data.data?.success) return [];

  const followers = data.data.result?.followers ?? [];

  return followers.map((f: Follower) => ({
    triggeredAt: new Date().toISOString(),
    eventType: "follow.created",
    body: {
      user: {
        fid: f.fid,
        username: f.username,
        display_name: f.displayName,
        pfp_url: f.pfpUrl,
      },
    },
    _authorUsername: f.username,
    _authorDisplayName: f.displayName,
    _authorPfpUrl: f.pfpUrl,
    _followerCount: f.followerCount,
  }));
};
